import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface Inequality {
  type: 'horizontal' | 'vertical';
  row: number;
  col: number;
  sign: '>' | '<';
}

interface FutoshikiPuzzle {
  board: number[];
  solution: number[];
  inequalities: Inequality[];
  size: number;
  difficulty: number;
  seed: number;
}

export default function FutoshikiGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [puzzle, setPuzzle] = useState<FutoshikiPuzzle | null>(null);
  const [userBoard, setUserBoard] = useState<number[]>([]);
  const [givenCells, setGivenCells] = useState<Set<number>>(new Set());
  const [selectedCell, setSelectedCell] = useState<number | null>(null);
  const [history, setHistory] = useState<number[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [startTime] = useState(Date.now());
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const dificultadOrden = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('futoshiki', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();
  const isFirstLoad = useRef(true);
  const [forceReload, setForceReload] = useState(0);

  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 16);
      setBoardH(clientHeight - 16);
    }
  };

  useEffect(() => {
    if (!loading) requestAnimationFrame(measureBoard);
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);

  // Mapeo de dificultad a tamaño
  const getSizeFromDificultad = (orden: number): number => {
    const sizeMap: Record<number, number> = {
      1: 4,  // Neurona
      2: 5,  // Sinapsis
      3: 6,  // Cerebro
      4: 7,  // Genio
      5: 7,  // Einstein (mismo tamaño, más difícil)
    };
    return sizeMap[orden] || 5;
  };

  // Cargar puzzle
  useEffect(() => {
    const loadPuzzle = async () => {
      try {
        setLoading(true);
        setError('');

        const size = getSizeFromDificultad(dificultadOrden);
        const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : undefined;
        const isNewGame = forceReload > 0;
        const response = await api.get('/futoshiki/generate', {
          params: { size, ...(!isNewGame && seedParam !== undefined && { seed: seedParam }) }
        });

        const puzzleData: FutoshikiPuzzle = response.data.data;
        setPuzzle(puzzleData);
        setSearchParams({ seed: puzzleData.seed.toString() }, { replace: true });

        // Inicializar board de usuario con las celdas dadas
        const initialBoard = [...puzzleData.board];
        setUserBoard(initialBoard);

        // Marcar celdas dadas (no editables)
        const given = new Set<number>();
        puzzleData.board.forEach((value, index) => {
          if (value > 0) {
            given.add(index);
          }
        });
        setGivenCells(given);

        // Inicializar historial
        setHistory([JSON.parse(JSON.stringify(initialBoard))]);
        setHistoryIndex(0);
        setIsCompleted(false);
        setShowVictory(false);
        setSelectedCell(null);
      } catch (err: any) {
        console.error('Error al cargar puzzle:', err);
        setError(err.response?.data?.error || 'Error al cargar el puzzle');
      } finally {
        setLoading(false);
        isFirstLoad.current = false;
      }
    };

    loadPuzzle();
  }, [dificultadOrden, forceReload]);

  // Guardar estado en historial
  const saveState = (newState: number[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newState)));

    if (newHistory.length > 50) {
      newHistory.shift();
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    } else {
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  };

  // Manejar teclas
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!puzzle || selectedCell === null || givenCells.has(selectedCell)) return;

      const size = puzzle.size;
      const row = Math.floor(selectedCell / size);
      const col = selectedCell % size;

      // Números del 1 al tamaño
      if (e.key >= '1' && e.key <= '9') {
        const num = parseInt(e.key);
        if (num <= size) {
          setCellValue(selectedCell, num);
        }
      }
      // Borrar
      else if (e.key === '0' || e.key === 'Backspace' || e.key === 'Delete' || e.key === ' ') {
        e.preventDefault();
        setCellValue(selectedCell, 0);
      }
      // Navegación con flechas
      else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        let newRow = row;
        let newCol = col;

        switch (e.key) {
          case 'ArrowUp':
            newRow = Math.max(0, row - 1);
            break;
          case 'ArrowDown':
            newRow = Math.min(size - 1, row + 1);
            break;
          case 'ArrowLeft':
            newCol = Math.max(0, col - 1);
            break;
          case 'ArrowRight':
            newCol = Math.min(size - 1, col + 1);
            break;
        }

        const newIndex = newRow * size + newCol;
        if (newIndex !== selectedCell) {
          setSelectedCell(newIndex);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [puzzle, selectedCell, givenCells]);

  // Establecer valor de celda
  const setCellValue = (index: number, value: number) => {
    if (givenCells.has(index) || !puzzle) return;

    const newBoard = [...userBoard];
    newBoard[index] = value;
    setUserBoard(newBoard);
    saveState(newBoard);

    // Auto-verificar si está completo
    setTimeout(() => autoCheckSolution(newBoard), 100);
  };

  // Auto-verificar solución
  const autoCheckSolution = (board: number[]) => {
    if (!puzzle) return;

    // Solo verificar si está completo
    const isComplete = !board.includes(0);
    if (!isComplete) return;

    // Verificar que sea correcto comparando con la solución
    const isCorrect = board.every((val, idx) => val === puzzle.solution[idx]);

    if (isCorrect && !isCompleted) {
      setIsCompleted(true);
      const tiempo = Math.floor((Date.now() - startTime) / 1000);
      saveProgress(tiempo);
      setShowVictory(true);
    }
  };

  // Deshacer
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setUserBoard(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  };

  // Rehacer
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setUserBoard(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  };

  // Pista
  const handleHint = () => {
    if (!puzzle) return;

    // Encontrar primera celda incorrecta o vacía
    for (let i = 0; i < userBoard.length; i++) {
      if (!givenCells.has(i) && userBoard[i] !== puzzle.solution[i]) {
        const newBoard = [...userBoard];
        newBoard[i] = puzzle.solution[i];
        setUserBoard(newBoard);
        saveState(newBoard);
        setGivenCells(new Set([...givenCells, i]));
        setError('💡 Pista añadida');
        setTimeout(() => setError(''), 2000);
        return;
      }
    }

    setError('¡El puzzle ya está resuelto correctamente!');
    setTimeout(() => setError(''), 2000);
  };

  // Mostrar solución
  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Estás seguro? Esto mostrará la solución completa.')) return;

    setUserBoard([...puzzle.solution]);
    setHistory([[...puzzle.solution]]);
    setHistoryIndex(0);
    setError('Solución mostrada');
    setTimeout(() => setError(''), 3000);
  };

  // Reiniciar
  const handleReset = () => {
    if (!puzzle || !window.confirm('¿Estás seguro de que quieres reiniciar el tablero?')) return;

    const resetBoard = [...puzzle.board];
    setUserBoard(resetBoard);
    setHistory([JSON.parse(JSON.stringify(resetBoard))]);
    setHistoryIndex(0);
    setIsCompleted(false);
    setShowVictory(false);
    setSelectedCell(null);
    setError('');

    // Restaurar solo las celdas dadas originales
    const given = new Set<number>();
    puzzle.board.forEach((value, index) => {
      if (value > 0) {
        given.add(index);
      }
    });
    setGivenCells(given);
  };

  // Guardar progreso
  const saveProgress = async (tiempo: number) => {
    if (!puzzle) return;

    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: puzzle.seed,
        tiempo,
        movimientos: 0,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos: 0,
        completado: true,
        datos_juego: {
          tablero_id: puzzle.seed,
          size: puzzle.size,
        },
      });
    } catch (error) {
      console.error('Error al guardar progreso:', error);
    }
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(board: number[], puz: FutoshikiPuzzle): Set<number> {
    const errores = new Set<number>();
    const { size, inequalities } = puz;

    // 1. Duplicados en fila
    for (let r = 0; r < size; r++) {
      const seen = new Map<number, number>();
      for (let c = 0; c < size; c++) {
        const v = board[r * size + c];
        if (!v) continue;
        if (seen.has(v)) { errores.add(r * size + seen.get(v)!); errores.add(r * size + c); }
        else seen.set(v, c);
      }
    }

    // 2. Duplicados en columna
    for (let c = 0; c < size; c++) {
      const seen = new Map<number, number>();
      for (let r = 0; r < size; r++) {
        const v = board[r * size + c];
        if (!v) continue;
        if (seen.has(v)) { errores.add(seen.get(v)! * size + c); errores.add(r * size + c); }
        else seen.set(v, r);
      }
    }

    // 3. Desigualdades violadas (solo si ambas celdas tienen valor)
    for (const ineq of inequalities) {
      let idxA: number, idxB: number;
      if (ineq.type === 'horizontal') {
        idxA = ineq.row * size + ineq.col;
        idxB = ineq.row * size + (ineq.col + 1);
      } else {
        idxA = ineq.row * size + ineq.col;
        idxB = (ineq.row + 1) * size + ineq.col;
      }
      const a = board[idxA], b = board[idxB];
      if (!a || !b) continue;
      const violated = ineq.sign === '>' ? a <= b : a >= b;
      if (violated) { errores.add(idxA); errores.add(idxB); }
    }

    return errores;
  }

  // Calcular tamaño de celda dinámicamente (rectangular para aprovechar todo el espacio)
  // TILE_W/TILE_H son independientes para usar todo el ancho Y el alto disponibles
  // Total width  = TILE_W * size + GAP_W * (size-1) + BORDER_W * 2  = TILE_W * (size*1.5 + 0.5) = boardW
  // Total height = TILE_H * size + GAP_H * (size-1) + BORDER_H * 2  = TILE_H * (size*1.5 + 0.5) = boardH
  const _size = puzzle?.size ?? 6;
  const TILE_W = boardW > 0 ? boardW / (_size * 1.5 + 0.5) : 60;
  const TILE_H = boardH > 0 ? boardH / (_size * 1.5 + 0.5) : 60;
  const GAP_W = TILE_W / 2;
  const GAP_H = TILE_H / 2;
  const BORDER_W = TILE_W / 2;
  const BORDER_H = TILE_H / 2;
  const fontSize = Math.floor(Math.min(TILE_W, TILE_H) * 0.5);
  const padEnabled = selectedCell !== null && !givenCells.has(selectedCell);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">❌ Error al cargar puzzle</div>
      </div>
    );
  }

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  const errores = modoAsistido ? calcularErrores(userBoard, puzzle) : new Set<number>();

  return (
    <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col">
      {/* Banner de Sesión */}
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">⚖️ Futoshiki</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (dificultadOrden === d.orden) setForceReload(prev => prev + 1);
                else navigate(`/juegos/futoshiki/${d.orden}`);
              }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dificultadOrden === d.orden ? 'bg-white text-purple-700' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          <button onClick={handleUndo} disabled={historyIndex <= 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          <button onClick={handleRedo} disabled={historyIndex >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          <button onClick={handleShowSolution} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={() => { setSearchParams({}); setForceReload(prev => prev + 1); }} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      {/* Board area */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex items-center justify-center p-2">
        <div
          className="relative bg-gray-100 rounded-lg"
          style={{
            width: `${TILE_W * puzzle.size + GAP_W * (puzzle.size - 1) + BORDER_W * 2}px`,
            height: `${TILE_H * puzzle.size + GAP_H * (puzzle.size - 1) + BORDER_H * 2}px`,
          }}
        >
          {/* Celdas */}
          {Array.from({ length: puzzle.size }, (_, row) =>
            Array.from({ length: puzzle.size }, (_, col) => {
              const index = row * puzzle.size + col;
              const isGiven = givenCells.has(index);
              const value = userBoard[index];
              const isSelected = selectedCell === index;
              const hasError = errores.has(index);

              const left = col * (TILE_W + GAP_W) + BORDER_W;
              const top = row * (TILE_H + GAP_H) + BORDER_H;

              let bgColor = '#ffffff';
              let borderColor = '#d1d5db';
              let textColor = '#111827';
              let cursor = 'pointer';
              if (isGiven) {
                bgColor = '#fde68a'; borderColor = '#b45309'; textColor = '#92400e'; cursor = 'default';
              } else if (hasError && isSelected) {
                bgColor = '#fca5a5'; borderColor = '#ef4444'; textColor = '#7f1d1d';
              } else if (hasError) {
                bgColor = '#fecaca'; borderColor = '#f87171'; textColor = '#991b1b';
              } else if (isSelected) {
                bgColor = '#bfdbfe'; borderColor = '#3b82f6'; textColor = '#1e3a8a';
              } else if (value > 0) {
                bgColor = '#bbf7d0'; borderColor = '#86efac'; textColor = '#166534';
              }

              return (
                <div
                  key={`cell-${row}-${col}`}
                  onClick={() => !isGiven && setSelectedCell(index)}
                  className="absolute flex items-center justify-center rounded transition-all border-2"
                  style={{
                    left: `${left}px`,
                    top: `${top}px`,
                    width: `${TILE_W}px`,
                    height: `${TILE_H}px`,
                    fontSize: `${fontSize}px`,
                    backgroundColor: bgColor,
                    borderColor,
                    color: textColor,
                    cursor,
                    fontWeight: isGiven ? 'bold' : undefined,
                  }}
                >
                  {value > 0 ? value : ''}
                </div>
              );
            })
          )}

          {/* Desigualdades */}
          {puzzle.inequalities.map((ineq, idx) => {
            // Detectar si esta desigualdad está violada
            let ineqError = false;
            if (modoAsistido) {
              const idxA = ineq.type === 'horizontal'
                ? ineq.row * puzzle.size + ineq.col
                : ineq.row * puzzle.size + ineq.col;
              const idxB = ineq.type === 'horizontal'
                ? ineq.row * puzzle.size + (ineq.col + 1)
                : (ineq.row + 1) * puzzle.size + ineq.col;
              const a = userBoard[idxA], b = userBoard[idxB];
              if (a && b) ineqError = ineq.sign === '>' ? a <= b : a >= b;
            }

            if (ineq.type === 'horizontal') {
              const left = ineq.col * (TILE_W + GAP_W) + BORDER_W + TILE_W;
              const top = ineq.row * (TILE_H + GAP_H) + BORDER_H;

              return (
                <div
                  key={`ineq-${idx}`}
                  className={`absolute flex items-center justify-center font-bold ${ineqError ? 'text-red-500' : 'text-purple-600'}`}
                  style={{
                    left: `${left}px`,
                    top: `${top}px`,
                    width: `${GAP_W}px`,
                    height: `${TILE_H}px`,
                    fontSize: `${Math.floor(Math.min(GAP_W, TILE_H) * 0.7)}px`,
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                >
                  {ineq.sign}
                </div>
              );
            } else {
              const left = ineq.col * (TILE_W + GAP_W) + BORDER_W;
              const top = ineq.row * (TILE_H + GAP_H) + BORDER_H + TILE_H;

              return (
                <div
                  key={`ineq-${idx}`}
                  className={`absolute flex items-center justify-center font-bold ${ineqError ? 'text-red-500' : 'text-purple-600'}`}
                  style={{
                    left: `${left}px`,
                    top: `${top}px`,
                    width: `${TILE_W}px`,
                    height: `${GAP_H}px`,
                    fontSize: `${Math.floor(Math.min(GAP_W, TILE_H) * 0.7)}px`,
                    transform: 'rotate(90deg)',
                    transformOrigin: 'center',
                    overflow: 'visible',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                >
                  {ineq.sign}
                </div>
              );
            }
          })}
        </div>
      </div>

      {/* Pad numérico virtual */}
      {/* Siempre presente: si apareciera solo al seleccionar, el tablero daria
          un salto al encogerse el hueco en el que esta centrado. */}
      {puzzle && (
        <div className={`bg-black/10 px-3 pt-2 pb-safe shrink-0 flex items-center justify-center gap-2 transition-opacity ${padEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
          {Array.from({ length: puzzle.size }, (_, i) => i + 1).map(num => (
            <button key={num} onClick={() => { if (padEnabled) setCellValue(selectedCell!, num); }} disabled={!padEnabled}
              className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-bold rounded-lg shadow active:scale-95 transition-all text-lg">
              {num}
            </button>
          ))}
          <button onClick={() => { if (padEnabled) setCellValue(selectedCell!, 0); }} disabled={!padEnabled} title="Borrar"
            className="w-11 h-11 ml-2 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg shadow active:scale-95 transition-all">
            ✕
          </button>
        </div>
      )}

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Futoshiki"
        gameIcon="⚖️"
        objective="Rellena el tablero con números respetando las desigualdades y sin repetir ningún número en filas ni columnas."
        rules={[
          'Cada fila y columna debe contener exactamente una vez cada número del 1 al N',
          'Respeta los signos de desigualdad (< y >) entre celdas adyacentes: el número mayor debe estar en el lado correcto',
          'Las celdas con número ya colocado son pistas fijas que no se pueden cambiar',
        ]}
        howToPlay="Toca una celda para seleccionarla y usa el teclado o el panel numérico inferior para colocar un número. Pulsa de nuevo o 0 para borrar."
        author="Nikoli"
      />

      {/* Modal de Victoria */}
      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: 0,
        }}
      />
    </div>
  );
}
